import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetSingleLegJobs } from 'api/kpi/kpi';
import { KPISingleLegJobsRow } from 'api/model';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

const Cells = ({ row }: { row: KPISingleLegJobsRow }) => (
  <>
    <td style={tdNumStyle}>{nf(row.jobs)}</td>
    <td style={tdNumStyle}>{nf(row.local)}</td>
    <td style={tdNumStyle}>{nf(row.otherCounty)}</td>
    <td style={tdNumStyle}>{nf(row.noHomeAnchor)}</td>
    <td style={tdNumStyle}>{pct(row.localPercentage)}</td>
  </>
);

/**
 * Enbenskörningar — ärenden med ett enda bilbärande ben (ingen mellanlandning, inget skifte;
 * persontransport får finnas) — per från-län, uppdelat på om FT hade hemankare i länet. Se
 * OperationsKpiLogic.GetSingleLegJobs.
 */
const SingleLegJobsWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetSingleLegJobs();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.SingleLegJobs]}
      count={result?.jobs}
      isLoading={getData.isLoading}
      info="Utförda ärenden där bilen kördes i ett enda ben: ingen mellanlandning och inget skifte. Persontransport till och från får finnas. Län = benets från-län. Inom länet = fälttestaren har ett hemankare i från-länet. Enbensärenden på lastbil eller med extern lastbilstransportör räknas inte, liksom ärendetyper utan bil."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!result || result.jobs === 0}
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{pct(result?.localPercentage)}</strong> av{' '}
          {nf(result?.jobs ?? 0)} enbenskörningar gjordes av FT med hemankare i
          länet
        </p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Från-län</th>
              <th style={thNumStyle}>Enben</th>
              <th style={thNumStyle}>Inom länet</th>
              <th style={thNumStyle}>Utom länet</th>
              <th style={thNumStyle}>Utan hemankare</th>
              <th style={thNumStyle}>Andel inom</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byCounty ?? []).map((row) => (
              <tr
                key={row.countyName}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <td style={tdStyle}>{row.countyName}</td>
                <Cells row={row} />
              </tr>
            ))}
            {result && (
              <tr style={totalRowStyle}>
                <td style={tdStyle}>Totalt</td>
                <Cells
                  row={{
                    countyName: '',
                    jobs: result.jobs,
                    local: result.local,
                    otherCounty: result.otherCounty,
                    noHomeAnchor: result.noHomeAnchor,
                    localPercentage: result.localPercentage,
                  }}
                />
              </tr>
            )}
          </tbody>
        </table>
        <ControlFigures
          items={[
            `${nf(result?.multiLegCases ?? 0)} utförda ärenden med flera ben (mellanlandning/skifte)`,
            result?.truckJobs
              ? `${nf(result.truckJobs)} enbensärenden på lastbil räknas inte`
              : null,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default SingleLegJobsWidget;
